/**
 * Portfolio Holding Detail Dialog (Portfolio Intelligence Phase 1).
 *
 * Opened via the Portfolio Tracker table's "View Details" action. Every
 * value shown here is computed CLIENT-SIDE from fields the list endpoint
 * already returns on the row (`average_buy_price`, `current_price`,
 * `target_price`, `target_upside_pct`) — no new endpoint, no new backend
 * calculation. Purely informational: never an order-placement surface, and
 * every string here is deliberately a noun-phrase/heading (e.g. "Review
 * Profit Booking"), never an imperative BUY/SELL/HOLD/EXIT instruction.
 */
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { PortfolioHoldingRow } from "@/types";

function num(value: string | number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// Mirrors the backend's own "near target" window (0% <= upside <= 5%) —
// see `PortfolioQueryService._NEAR_TARGET_LOW_PCT`/`_HIGH_PCT`.
const NEAR_TARGET_LOW = 0;
const NEAR_TARGET_HIGH = 5;

/** Profit % = (current_price - average_buy_price) / average_buy_price *
 * 100 — computed client-side from fields the row already carries, same
 * pattern as `targetState`/`targetProgressPct` below. `null` when either
 * value is unavailable. */
function profitPct(row: PortfolioHoldingRow): number | null {
  if (!row.current_price) return null;
  const avg = Number.parseFloat(row.average_buy_price);
  const current = Number.parseFloat(row.current_price);
  if (Number.isNaN(avg) || Number.isNaN(current) || avg === 0) return null;
  return Math.round(((current - avg) / avg) * 100 * 100) / 100;
}

/** Whether the current price has reached/passed the further-out Profit
 * Review Level (Phase 1B) — surfaces "Review Profit Booking" in addition
 * to (not instead of) the existing Target Reached state below. `null`
 * when `profit_review_level` itself is `null` (not enough price history). */
function profitReviewReached(row: PortfolioHoldingRow): boolean | null {
  if (!row.profit_review_level || !row.current_price) return null;
  const level = Number.parseFloat(row.profit_review_level);
  const current = Number.parseFloat(row.current_price);
  if (Number.isNaN(level) || Number.isNaN(current)) return null;
  return current >= level;
}

type TargetState = "reached" | "approaching" | "pending" | "unavailable";

function targetState(row: PortfolioHoldingRow): TargetState {
  if (row.target_price === null || row.target_price === undefined) return "unavailable";
  const current = row.current_price ? Number.parseFloat(row.current_price) : null;
  const target = Number.parseFloat(row.target_price);
  if (current === null || Number.isNaN(current) || Number.isNaN(target)) return "unavailable";
  if (current >= target) return "reached";
  if (
    row.target_upside_pct !== null &&
    row.target_upside_pct !== undefined &&
    row.target_upside_pct >= NEAR_TARGET_LOW &&
    row.target_upside_pct <= NEAR_TARGET_HIGH
  ) {
    return "approaching";
  }
  return "pending";
}

/**
 * Target Progress: what fraction of the distance from Average Buy Price to
 * the Indicative Research Target has already been covered by the Current
 * Price, clamped to [0, 100]. Only meaningful when the target is actually
 * ABOVE the average buy price (a positive denominator) — when it isn't
 * (e.g. a target that has fallen below cost basis on a weak ATR reading),
 * "progress toward target" isn't a sensible percentage, so this returns
 * `null` and the UI shows "—" rather than a nonsensical or negative number.
 */
function targetProgressPct(row: PortfolioHoldingRow): number | null {
  if (!row.target_price || !row.current_price) return null;
  const avg = Number.parseFloat(row.average_buy_price);
  const target = Number.parseFloat(row.target_price);
  const current = Number.parseFloat(row.current_price);
  if ([avg, target, current].some((v) => Number.isNaN(v))) return null;
  if (target <= avg) return null;
  const raw = ((current - avg) / (target - avg)) * 100;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

export function PortfolioHoldingDetailDialog({
  row,
  onClose,
  onEdit,
}: {
  row: PortfolioHoldingRow;
  onClose: () => void;
  onEdit: () => void;
}) {
  const state = targetState(row);
  const progress = targetProgressPct(row);
  const profit = profitPct(row);
  const reviewLevelReached = profitReviewReached(row);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>
          {row.symbol} — {row.company_name}
        </span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={0.5}>
          {(state === "reached" || reviewLevelReached) && (
            <Alert severity="success" variant="outlined">
              <Typography variant="subtitle2" fontWeight={700}>
                Review Profit Booking
              </Typography>
              <Typography variant="body2">
                {reviewLevelReached
                  ? "The current price has reached or passed the further-out Profit Review Level for this holding — purely informational, worth a closer look at your position."
                  : "The current price has reached or passed the Indicative Research Target for this holding — purely informational, worth a closer look at your position."}
              </Typography>
            </Alert>
          )}

          {(row.accumulation_range_low || row.accumulation_range_high) && (
            <Alert severity="info" variant="outlined">
              <Typography variant="subtitle2" fontWeight={700}>
                Accumulation Range (Research Only)
              </Typography>
              <Typography variant="body2">
                ₹{num(row.accumulation_range_low)} – ₹{num(row.accumulation_range_high)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                An informational research range for this holding's current zone — not a buy recommendation
                or a guaranteed price.
              </Typography>
            </Alert>
          )}

          <Stack spacing={0.75}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Average Buy Price
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ₹{num(row.average_buy_price)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Current Price
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.current_price ? `₹${num(row.current_price)}` : "—"}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Profit %
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={profit !== null && profit < 0 ? "error.main" : undefined}
              >
                {profit !== null ? `${profit > 0 ? "+" : ""}${profit}%` : "—"}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Indicative Research Target
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.target_price ? `₹${num(row.target_price)}` : "Not available"}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                % Distance to Target
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.target_upside_pct !== null && row.target_upside_pct !== undefined
                  ? `${row.target_upside_pct > 0 ? "+" : ""}${row.target_upside_pct}%`
                  : "—"}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Profit Review Level
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.profit_review_level ? `₹${num(row.profit_review_level)}` : "Not available"}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Target Progress
            </Typography>
            {progress !== null ? (
              <>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">
                  {progress}% of the distance from Average Buy Price to the Indicative Research Target
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                — (not meaningful for this holding — see status below)
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Status
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {state === "reached" && "Target Reached"}
              {state === "approaching" && "Approaching Target"}
              {state === "pending" && "Pending"}
              {state === "unavailable" && "Target not available"}
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.85 }}>
            Informational and research-oriented only — not a guaranteed price, a recommendation, or a
            solicitation to buy or sell any security.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onEdit}>Edit Holding</Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PortfolioHoldingDetailDialog;
